/**
 * One play session of one track: a fresh canvas and Engine (fresh physics
 * world and GPU context) with the game HUD, sound cues and progress tracking.
 * `dispose()` tears all of it down; the app creates a new session for every
 * track it opens, so nothing carries over between tracks.
 *
 * Record integrity: the engine runs without developer tools, and records go
 * through CompetitiveRecords, which only accepts the track's baseline record
 * key (shipped movement rules). Validity itself (full run, no death, no pause,
 * no stall) stays the simulation's decision.
 */
import { Engine, type EngineTestHooks } from '../core/Engine';
import { startRefusedText, type GameMode } from '../gameplay/AttemptManager';
import { CompetitiveRecords, type ProgressStore } from '../gameplay/ProgressStore';
import type { AudioEngine } from '../audio/AudioEngine';
import { MovementSounds } from '../audio/MovementSounds';
import { GameHud } from '../ui/GameHud';
import { gameBindings, type Settings } from '../ui/Settings';
import type { DeathCause } from '../movement/PlayerState';
import type { CatalogTrack } from './catalog';
import { ineligibleReasons, type IneligibleReason, type Split } from '../replay/RunTiming';
import { GhostRacer } from '../replay/GhostRacer';
import type { RunRecording } from '../replay/RecordingFormat';
import type { ReplayLibrary } from '../replay/ReplayLibrary';

export interface RunResult {
  trackKey: string;
  seconds: number;
  mode: GameMode;
  /** Counted as a record run (full run, no deaths, not invalidated). */
  valid: boolean;
  newBest: boolean;
  /** Best before this run (null on a first completion). */
  previousBest: number | null;
  splits: Split[];
  /** Splits of the best run before this one, for comparison. */
  previousBestSplits: Split[];
  /** Best time per sector before this run (RunTiming.sectorKey → seconds). */
  previousBestSectors: Record<string, number>;
  deaths: number;
  /** Why the run does not count (empty when it does). */
  ineligible: IneligibleReason[];
}

export interface SessionCallbacks {
  /** The engine paused (mouse released) or resumed. */
  onPauseChange(paused: boolean, message: string): void;
  onFinish(result: RunResult): void;
  /** A new attempt began (restart, death in a full run, mode change). */
  onAttemptReset(): void;
}

export interface SessionTestHooks extends EngineTestHooks {
  recordKey(): string;
  baselineKey(): string;
  ghost(): ReturnType<GhostRacer['describe']>;
}

const DEATH_TEXT: Readonly<Record<DeathCause, string>> = {
  'lethal-surface': 'Lethal surface',
  'out-of-bounds': 'Fell off the course',
  'numerical-fault': 'Movement fault',
};

export class GameSession {
  private readonly sounds: MovementSounds;
  private ghosts: GhostRacer | null = null;
  private readonly unsubscribe: (() => void)[] = [];
  private previousBest: number | null = null;
  private previousBestSplits: Split[] = [];
  private previousBestSectors: Record<string, number> = {};
  private disposed = false;

  private constructor(
    readonly track: CatalogTrack,
    private readonly engine: Engine,
    private readonly hud: GameHud,
    private readonly canvas: HTMLCanvasElement,
    private readonly progress: ProgressStore,
    audio: AudioEngine,
    callbacks: SessionCallbacks,
    settings: Readonly<Settings>,
  ) {
    const sim = engine.sim;
    this.sounds = new MovementSounds(sim, audio);
    this.sounds.speedSound = settings.speedSound;
    const key = track.recordKey;
    this.snapshotBest();
    const now = (): number => performance.now();
    this.unsubscribe.push(
      sim.events.on('attempt-start', (e) => {
        this.snapshotBest();
        if (e.mode === 'full-run' && sim.recordKey() === key) progress.attemptStarted(key);
      }),
      sim.events.on('attempt-reset', () => callbacks.onAttemptReset()),
      sim.events.on('attempt-invalidated', (e) => hud.showBanner('invalid', `Run interrupted (${e.reason}): not recorded`, now())),
      sim.events.on('start-refused', (e) => hud.showBanner('invalid', startRefusedText(e), now())),
      sim.events.on('finish-unrecorded', (e) => hud.showBanner('invalid', `Finished, but not recorded: the run was interrupted (${e.reason}). R restarts.`, now())),
      sim.events.on('checkpoint', () => hud.showSplit(sim.attempts.splits, now())),
      sim.events.on('death', (e) => {
        const back = e.mode === 'full-run' ? 'back to the start' : sim.attempts.lastCheckpointIndex === null ? 'back to the start' : 'back to the checkpoint';
        hud.showBanner('death', `${DEATH_TEXT[e.cause]} · ${back}`, now());
      }),
      sim.events.on('finish', (e) => {
        const splits = sim.attempts.splits.map((s) => ({ ...s }));
        const ineligible = ineligibleReasons({ mode: e.mode, deaths: e.deaths, interrupted: false, baselineRules: sim.recordKey() === key });
        if (ineligible.length === 0) progress.completed(key, e.seconds, splits);
        if (ineligible.length === 0 && e.newBest) this.ghosts?.markPersonalBest();
        hud.showBanner('finish', ineligible.length === 0 ? (e.newBest ? 'New personal best' : 'Finished') : `Finished · not recorded (${ineligible[0]})`, now());
        callbacks.onFinish({
          trackKey: track.key,
          seconds: e.seconds,
          mode: e.mode,
          valid: ineligible.length === 0,
          newBest: e.newBest,
          previousBest: this.previousBest,
          splits,
          previousBestSplits: this.previousBestSplits,
          previousBestSectors: this.previousBestSectors,
          deaths: e.deaths,
          ineligible,
        });
      }),
    );
  }

  static async start(
    host: HTMLElement,
    track: CatalogTrack,
    opts: { settings: Readonly<Settings>; progress: ProgressStore; audio: AudioEngine; mode: GameMode; callbacks: SessionCallbacks; library?: ReplayLibrary | null },
  ): Promise<GameSession> {
    if (!track.level) throw new Error(track.error ?? 'This track cannot be played.');
    const canvas = document.createElement('canvas');
    canvas.className = 'game-canvas';
    canvas.tabIndex = 0;
    canvas.setAttribute('aria-label', `${track.title}: game view`);
    const debug = document.createElement('pre');
    debug.className = 'game-debug';
    debug.hidden = true;
    const flash = document.createElement('div');
    const textHud = document.createElement('div');
    host.replaceChildren(canvas, debug);
    const hud = new GameHud(host, { layout: opts.settings.hud, flashes: opts.settings.flashes });
    let session: GameSession | null = null;
    let engine: Engine;
    try {
      engine = await Engine.create({ canvas, hud: textHud, flash, debug, app: host }, track.level, {
        records: new CompetitiveRecords(opts.progress, track.recordKey),
        settings: { ...opts.settings },
        bindings: gameBindings(opts.settings.bindings),
        devTools: false,
        hud,
        startMode: opts.mode,
        onPauseChange: (paused, message) => session?.pauseChanged(paused, message, opts.callbacks),
        onTick: () => {
          session?.sounds.afterTick();
          session?.ghosts?.afterTick();
        },
        onRender: (alpha) => session?.ghosts?.render(alpha),
      });
    } catch (err: unknown) {
      hud.dispose();
      host.replaceChildren();
      throw err;
    }
    session = new GameSession(track, engine, hud, canvas, opts.progress, opts.audio, opts.callbacks, opts.settings);
    session.startGhosts(opts.library ?? null, opts.settings);
    engine.start();
    session.frameLoop();
    return session;
  }

  get mode(): GameMode {
    return this.engine.sim.mode;
  }

  get paused(): boolean {
    return this.engine.isPaused;
  }

  get finished(): boolean {
    return this.engine.sim.attempts.status === 'finished';
  }

  /** Takes the mouse and starts or resumes play. Call from a click or key press. */
  play(): Promise<void> {
    return this.engine.requestPlay();
  }

  release(): void {
    this.engine.releasePointer();
  }

  restart(toStart = true): void {
    this.engine.restartRun(toStart);
  }

  setMode(mode: GameMode): void {
    this.engine.setMode(mode);
  }

  applySettings(settings: Readonly<Settings>): void {
    this.engine.applySettings(settings);
    this.engine.setBindings(gameBindings(settings.bindings));
    this.hud.setOptions({ layout: settings.hud, flashes: settings.flashes });
    this.sounds.speedSound = settings.speedSound;
    this.ghosts?.apply(settings.ghost, settings.ghostOpacity, settings.ghostOffset);
  }

  /** The most recent attempt's recording (null before any attempt started its timer). */
  latestRecording(): RunRecording | null {
    return this.ghosts?.latestRecording() ?? null;
  }

  testHooks(): SessionTestHooks {
    return {
      ...this.engine.testHooks(),
      recordKey: () => this.engine.sim.recordKey(),
      baselineKey: () => this.track.recordKey,
      ghost: () => this.ghosts?.describe() ?? { source: 'off', label: null, visible: false, pbLoaded: false, lastLoaded: false },
    };
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    cancelAnimationFrame(this.raf);
    for (const u of this.unsubscribe) u();
    this.ghosts?.dispose();
    this.sounds.dispose();
    this.engine.dispose();
    this.hud.dispose();
    this.canvas.remove();
  }

  private raf = 0;

  private startGhosts(library: ReplayLibrary | null, settings: Readonly<Settings>): void {
    const sim = this.engine.sim;
    const key = this.track.recordKey;
    this.ghosts = new GhostRacer({
      sim,
      trackKey: this.track.key,
      title: this.track.title,
      recordKey: key,
      library,
      ineligible: (f) => ineligibleReasons({ mode: f.mode, deaths: f.deaths, interrupted: false, baselineRules: sim.recordKey() === key }),
      addToScene: (view) => this.engine.addToScene(view.group),
      setLabel: (text) => this.hud.setGhostLabel(text),
      notice: (text) => this.hud.showBanner('invalid', text, performance.now()),
    });
    this.ghosts.apply(settings.ghost, settings.ghostOpacity, settings.ghostOffset);
  }

  /** Per-frame sound (separate from the engine's loop, which it only reads). */
  private frameLoop(): void {
    const step = (now: number): void => {
      if (this.disposed) return;
      this.sounds.frame(now, this.engine.isPaused);
      this.raf = requestAnimationFrame(step);
    };
    this.raf = requestAnimationFrame(step);
  }

  private pauseChanged(paused: boolean, message: string, callbacks: SessionCallbacks): void {
    if (paused) this.sounds.frame(performance.now(), true);
    callbacks.onPauseChange(paused, message);
  }

  private snapshotBest(): void {
    const p = this.progress.get(this.track.recordKey);
    this.previousBest = p.best;
    this.previousBestSplits = p.bestSplits;
    this.previousBestSectors = p.bestSectors;
    this.hud.setReference(p.bestSplits);
  }
}
